import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../Modal';
import { Button, Card } from '../ui';
import { DocDraftFields } from '../doc-viewer/DocDraftFields';
import { FolderChooserDialog } from './FolderChooserDialog';
import { draftBlocker } from '../../../lib/file-draft';
import { docViewerKind } from '../../../lib/doc-viewer';
import { useNarrow } from '../../../lib/use-narrow';
import { useTranslation } from '../../../lib/i18n';
import styles from './UploadDraftDialog.module.css';

// An upload is a file the user is FILING, not just bytes being posted somewhere.
//
// Uploading used to write straight into whichever folder the browser happened to be showing, under
// whatever name the file already had, with no description — and at the TOP of the tree, where there is no
// folder at all, it wrote nowhere and said nothing (reported: "it showed uploading but I don't see the
// PDF"). So the same three questions the editor asks about a new file are asked about an uploaded one:
// what is it called, what is it for, and where does it go.
//
// The fields are `DocDraftFields`, the SAME component the editor's create surface uses, so the two cannot
// drift into asking one question two ways. What this adds is the preview: the point of naming a file you
// just picked is being able to see which file it is.
export function UploadDraftDialog({
  file,
  defaultFolder,
  workspaceNames,
  busy,
  onCancel,
  onConfirm,
}: {
  file: File;
  /** The folder the browser was showing. `''` at the top of the tree, where the user must choose. */
  defaultFolder: string;
  workspaceNames: Record<string, string>;
  /** The write is in flight — the dialog stays up so the preview does not vanish under the user. */
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (draft: { name: string; description: string; folder: string }) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(file.name);
  const [description, setDescription] = useState('');
  const [folder, setFolder] = useState(defaultFolder);
  const [choosingFolder, setChoosingFolder] = useState(false);

  // A LOCAL preview: the bytes are already in the browser, so this renders instantly and costs no second
  // fetch of a file we just sent. Revoked on unmount — an object URL pins the whole file in memory.
  const previewUrl = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);

  const blocker = draftBlocker({ name, folder });
  const kind = docViewerKind(file.name, null, file.type);

  return (
    <>
      <Modal
        open
        onClose={busy ? () => undefined : onCancel}
        size="medium"
        maxHeightPx={650}
        scrollBody
        headerSeparator
        footerSeparator
        title={t('files.upload.action')}
        footer={
          <div className={styles.footer}>
            <Button variant="outlined" onClick={onCancel} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => onConfirm({ name, description: description.trim(), folder })}
              loading={busy}
              disabled={blocker !== null}
            >
              {t('files.upload.destinationConfirm')}
            </Button>
          </div>
        }
      >
        <div className={styles.body}>
          <FilePreview file={file} url={previewUrl} kind={kind} />
          <DocDraftFields
            name={name}
            onName={setName}
            description={description}
            onDescription={setDescription}
            folder={folder}
            workspaceNames={workspaceNames}
            onPickFolder={() => setChoosingFolder(true)}
            disabled={busy}
          />
        </div>
      </Modal>
      {choosingFolder && (
        <FolderChooserDialog
          title={t('files.new.locationLabel')}
          confirmLabel={t('files.new.locationConfirm')}
          startFolder={folder}
          onClose={() => setChoosingFolder(false)}
          onChoose={(chosen) => {
            setFolder(chosen);
            setChoosingFolder(false);
          }}
        />
      )}
    </>
  );
}

/** What the file looks like, at a glance. An image and a PDF show themselves; everything else states what
 *  it is, because a wall of decoded bytes is not a preview. The name is deliberately NOT repeated here —
 *  it is the field directly below, and the user is about to change it. */
function FilePreview({ file, url, kind }: { file: File; url: string; kind: string | null }) {
  const [broken, setBroken] = useState(false);
  const narrow = useNarrow();
  const sizeLabel = formatBytes(file.size);
  const extension = fileExtensionLabel(file.name);

  if (!broken && file.type.startsWith('image/')) {
    return (
      <Card variant="stone" disablePadding className={styles.preview}>
        {/* A bare `<img>`, not `next/image`: the source is an object URL for bytes already in this
            browser, so there is nothing for the image optimizer to fetch, size or cache. */}
        <img src={url} alt={file.name} className={styles.image} onError={() => setBroken(true)} />
      </Card>
    );
  }

  // The same rule the viewer applies (`PdfDocView`): iOS WebKit — hence every browser on iOS — renders
  // only a PDF's first page with no way to scroll, so an embedded PDF is a DESKTOP affordance. On a phone
  // it falls through to the summary below rather than showing a cropped, unscrollable sheet.
  if (!broken && kind === 'pdf' && !narrow) {
    return (
      <Card variant="stone" disablePadding className={styles.preview}>
        <embed src={url} type="application/pdf" className={styles.pdf} />
      </Card>
    );
  }

  return (
    <Card variant="stone" disablePadding className={`${styles.preview} ${styles.summary}`}>
      {extension && <span className={styles.summaryKind}>{extension}</span>}
      <span className={styles.summarySize}>{sizeLabel}</span>
    </Card>
  );
}

function fileExtensionLabel(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : '';
}

/** Bytes as the row already says them, so a file reads the same size here and in the list. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
